import React, { ElementType } from 'react'

import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
// import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import ListItemButton from '@mui/material/ListItemButton'
import { ListItemButtonProps } from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'

import Icon from 'src/components/ui/icon'

import Translations from 'src/layouts/components/Translations'
import { CustomPalette } from 'src/theme/palette'
import { NavLink as RouterNavLink } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { usePermission, PermissionPath } from 'src/hooks/usePermission'
import { useLayoutContext } from 'src/layouts/layoutContext'
import useResponsive from 'src/hooks/useResponsive'

interface Props {
  title: string
  path?: string
  icon?: React.ReactNode
  disabled?: string
  deep?: number
  children?: React.ReactNode
  permission?: PermissionPath
  onClick?: () => void
  content?: () => React.JSX.Element
  external?: boolean
}

const MenuNavLink = styled(ListItemButton)<
  ListItemButtonProps & {
    component?: ElementType
    to: string
    target?: '_blank' | undefined
  }
>(({ theme }) => ({
  width: '100%',

  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  overflow: 'hidden',
  transition:
    'padding-left .25s ease-in-out, padding-right .25s ease-in-out, background-color .18s ease, box-shadow .18s ease',
  color: `var(--left-nav-item-color, ${theme.palette.common.white})`,
  fill: `var(--left-nav-item-fill, ${theme.palette.common.white})`,

  '&:hover': {
    backgroundColor: `var(--left-nav-hover-bg, ${
      (theme.palette as CustomPalette).navigationMenu.hover
    })`,
    boxShadow: 'var(--left-nav-hover-shadow, none)',
  },
  '&.active': {
    color: `var(--left-nav-active-color, ${theme.palette.success.main})`,
    fill: `var(--left-nav-active-color, ${theme.palette.success.main})`,

    '&, &:hover': {
      // boxShadow: `0px 2px 6px ${hexToRGBA(theme.palette.secondary.main, 0.48)}`,
      background: `var(--left-nav-active-bg, ${
        (theme.palette as CustomPalette).navigationMenu.active
      })`,
      boxShadow: 'var(--left-nav-active-shadow, none)',

      '&.Mui-focusVisible': {
        background: `var(--left-nav-active-bg, ${
          (theme.palette as CustomPalette).navigationMenu.active
        })`,
      },
    },
    '& .MuiTypography-root, & svg': {
      color: `var(--left-nav-active-text-color, ${
        (theme.palette as CustomPalette).navigationMenu.textColor
      }) !important`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 6,
      top: 10,
      bottom: 10,
      width: 2,
      borderRadius: 4,
      backgroundColor: 'var(--left-nav-active-accent, currentColor)',
    },
  },
}))

export const NavItem = (props: Props) => {
  const {
    path,
    title,
    disabled,
    icon,
    children,
    content: Content,
    permission,
    onClick,
    deep = 0,
    external = false,
  } = props
  const isAccess = usePermission(permission)
  const { setNavHandle } = useLayoutContext()
  const { isMobile } = useResponsive()

  const [open, setOpen] = React.useState(false)
  const { pathname } = useLocation()
  const isNavLinkActive = pathname === path && path && !external

  const handleOpenContent = () => {
    setOpen((prev) => !prev)
  }

  if (!isAccess) return <></>

  return (
    <ListItem
      disablePadding={!isMobile}
      className="nav-group"
      sx={{
        mt: { xs: 0, md: 1 },
        px: '0 !important',
        flexDirection: 'column',

        ...(isMobile
          ? { paddingTop: 0 }
          : {
              '&:hover': {
                '.navItemCollapseIcon': {
                  opacity: 1,
                },
              },
            }),
      }}
    >
      <MenuNavLink
        component={path && !external ? RouterNavLink : 'a'}
        {...(disabled && { tabIndex: -1 })}
        className={isNavLinkActive ? 'active' : ''}
        to={path === undefined ? '/' : `${path}`}
        {...(external &&
          path && { href: path, target: '_blank', rel: 'noopener noreferrer' })}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault()
            e.stopPropagation()

            onClick()
          }
          if (path === undefined) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(!open)
          }

          if (isMobile && path && !external) {
            setNavHandle?.()
          }
        }}
        sx={{
          py: 3,
          px: 2 + deep * 1.5,
          position: 'relative',

          ...(disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
        }}
      >
        <Stack
          direction="row"
          justifyContent={'space-between'}
          sx={{ width: '100%', overflow: { xs: 'hidden', md: 'visible' } }}
        >
          <Stack
            className="railIconWrap"
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              minWidth: 47,
              height: 21,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            {icon}
            <Typography
              className="railLabel"
              color="inherit"
              noWrap
              fontSize={9}
              fontWeight={400}
              lineHeight={1.1}
              textAlign="center"
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: 21,
                left: '50%',
                width: 56,
                maxWidth: 56,
                overflow: 'hidden',
                pointerEvents: 'none',
                textOverflow: 'ellipsis',
              }}
            >
              <Translations text={title} />
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={'space-between'}
            flex={1}
          >
            <Typography
              className={'padding opacity'}
              color="inherit"
              noWrap
              fontSize={12}
              fontWeight={400}
            >
              <Translations text={title} />
            </Typography>
            {(children || Content) && (
              <Stack
                className="opacity"
                px={1}
                sx={{
                  transform: open ? 'rotate(-180deg)' : 'rotate(0deg)',
                }}
                onClick={
                  path
                    ? (event) => {
                        event.stopPropagation()
                        event.preventDefault()
                        handleOpenContent()
                      }
                    : undefined
                }
              >
                <Icon fontSize="1.125rem" icon={'tabler:chevron-down'} />
              </Stack>
            )}
          </Stack>
        </Stack>
      </MenuNavLink>
      {(children || Content) && (
        <Stack className="height disappear" sx={{ width: '100%' }}>
          <Collapse
            component="ul"
            onClick={(e) => e.stopPropagation()}
            in={open}
            sx={{
              p: 0,
              width: '100%',
            }}
          >
            {open && Content && <Content />}

            {open && children}
          </Collapse>
        </Stack>
      )}
    </ListItem>
  )
}
